import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { skipToken } from '@reduxjs/toolkit/query';
import { useNavigate } from 'react-router';
import DateRange from '@/components/date/DateRange';
import ClientDetailsFlyout from '@/components/flyouts/ClientDetailsFlyout/ClientDetailsFlyout';
import { dashboardMobileWidthValue, mobileWidth } from '@/constants';
import { DASHBOARD_WIDGET_DATE_PICKER_SX } from '@/constants/dashboardWidgetDatePickerSx';
import { paths } from '@/constants/paths';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useCanNavigateTo } from '@/hooks/useCanNavigateTo';
import { useLocalization } from '@/hooks/useLocalization';
import { useWidgetWidthIsBelow } from '@/hooks/useWidgetWidthIsBelow';
import { isEmbeddableWidgetBundle } from '@/integrations/embeddableWidgetPortalUtils';
import type {
  InvestorNetWorthChange,
  NetWorthChangeDirection,
  NetWorthChangePeriod,
} from '@/interfaces/netWorthChanges';
import type { StandardWidgetProps } from '@/models/WidgetModels';
import { buildAdvisorClientDetailUrl } from '@/modules/advisor/utils/clientDetailRouting';
import { useGetInvestorNetWorthChangesQuery } from '@/store/advisorNetWorthChangesApi';
import { ContentContainer } from '@/widgets/contentContainer';
import NetWorthChangesGrid from './NetWorthChangesGrid';
import { TOTAL_ROW_ID } from './netWorthChangeFormat';
import { periodForSelection } from './netWorthChangeSelection';
import {
  rankNetWorthChanges,
  searchNetWorthChanges,
  totalNetWorthChanges,
} from './rankNetWorthChanges';
import { useNetWorthChangeRangePicker } from './useNetWorthChangeRangePicker';
import { useNetWorthChangesPreferences } from './useNetWorthChangesPreferences';

/**
 * Advisor dashboard widget ranking assigned clients by how far their net worth
 * moved over a period: the Beta build of legacy's Biggest Net Worth Changes
 * (`advisor-daily-changes`, IU-99).
 *
 * Shows the ten biggest percentage moves in either direction, narrowed to
 * Gainers or Losers on request, with a footer totalling the rows on screen.
 * The range and the All / Gainers / Losers choice persist in the advisor's user
 * settings. Search narrows the ranked ten; it never re-ranks or widens them.
 *
 * A name takes the advisor to that client's detail page, as the ticket asks.
 * Inside the embeddable bundle, where routing goes nowhere, it opens the client
 * details flyout instead. Without access to the Clients page the name is plain
 * text.
 *
 * At half width, or once the widget is narrower than the dashboard's mobile
 * breakpoint, the grid becomes the stacked card list used on mobile.
 *
 * Reads `currentData` rather than `data`: while a new range is in flight,
 * `data` still holds the previous range's ranking, which would show under the
 * new dates.
 */
export default function BiggestNetWorthChangesWidget({
  title,
  isWidget = false,
  isHalfWidth = false,
  container = 'widget',
}: StandardWidgetProps = {}) {
  const { t } = useLocalization();
  const isMobile = useMediaQuery(mobileWidth);
  const authLoaded = useAuthIsLoaded();
  const navigate = useNavigate();
  const canNavigateToClients = useCanNavigateTo(paths.advisor.clients.index);
  const canOpenClient = isEmbeddableWidgetBundle || canNavigateToClients;
  const { selection, direction, setSelection, setDirection, isReady } =
    useNetWorthChangesPreferences();
  const picker = useNetWorthChangeRangePicker(selection, setSelection);
  const { ref: widgetWidthRef, isBelow: isBelowMobileWidth } =
    useWidgetWidthIsBelow(isWidget, dashboardMobileWidthValue);
  const isCompact =
    isMobile || (isWidget && isHalfWidth) || (isWidget && isBelowMobileWidth);
  const [search, setSearch] = useState('');
  const [openClientId, setOpenClientId] = useState<number | null>(null);

  const period: NetWorthChangePeriod | null = useMemo(
    () => periodForSelection(selection, picker.today),
    [selection, picker.today],
  );

  const { currentData, isFetching, isError, refetch } =
    useGetInvestorNetWorthChangesQuery(
      authLoaded && isReady && period ? period : skipToken,
    );

  const ranked = useMemo(
    () => rankNetWorthChanges(currentData?.Records ?? [], direction),
    [currentData, direction],
  );
  const visible = useMemo(
    () => searchNetWorthChanges(ranked, search),
    [ranked, search],
  );
  const totalRow: InvestorNetWorthChange = useMemo(() => {
    const totals = totalNetWorthChanges(visible);
    return {
      Id: TOTAL_ROW_ID,
      Name: t('netWorthChanges.total', 'Total:'),
      OldDollarBalance: totals.OldDollarBalance,
      DollarBalance: totals.DollarBalance,
      DollarChange: totals.DollarChange,
      PercentChange: totals.PercentChange ?? 0,
    };
  }, [visible, t]);

  const openClient = useCallback(
    (investorId: number) => {
      if (isEmbeddableWidgetBundle) {
        setOpenClientId(investorId);
        return;
      }
      void navigate(buildAdvisorClientDetailUrl(investorId));
    },
    [navigate],
  );

  const widgetTitle =
    title ?? t('widgetNetWorthChanges', 'Biggest Net Worth Changes');
  const isLoading = !authLoaded || !isReady || (isFetching && !currentData);
  const searchLabel = t('netWorthChanges.searchPlaceholder', 'Search by name');

  const renderBody = () => {
    if (isError) {
      return (
        <Stack
          alignItems='flex-start'
          gap={1.5}
        >
          <Typography color='error'>
            {t('errorLoadingData', 'Error loading data.')}
          </Typography>
          <Button
            variant='outlined'
            disabled={isFetching}
            onClick={() => {
              void refetch();
            }}
          >
            {t('retry', 'Retry')}
          </Button>
        </Stack>
      );
    }
    if (isLoading) {
      return (
        <Skeleton
          variant='rounded'
          height={320}
          data-testid='net-worth-changes-loading'
        />
      );
    }
    if (!period || ranked.length === 0) {
      return (
        <Typography color='text.secondary'>
          {t(
            'netWorthChanges.empty',
            'No client net worth changed over this range.',
          )}
        </Typography>
      );
    }
    if (visible.length === 0) {
      return (
        <Typography color='text.secondary'>
          {t('netWorthChanges.noMatches', 'No clients match "{{term}}".', {
            term: search.trim(),
          })}
        </Typography>
      );
    }
    return (
      <NetWorthChangesGrid
        rows={visible}
        totalRow={totalRow}
        period={period}
        isCompact={isCompact}
        canOpenClient={canOpenClient}
        onOpenClient={openClient}
      />
    );
  };

  return (
    <ContentContainer
      ref={widgetWidthRef}
      container={container}
      sx={{ width: '100%' }}
    >
      <Typography
        variant='h5'
        sx={{ mb: 1 }}
      >
        {widgetTitle}
      </Typography>

      <Stack
        direction='row'
        justifyContent='flex-end'
        alignItems='flex-end'
        flexWrap='wrap'
        gap={1}
        sx={{ mb: 2 }}
      >
        <ToggleButtonGroup
          exclusive
          size='small'
          value={direction}
          aria-label={t('netWorthChanges.show', 'Show')}
          onChange={(_event, next: NetWorthChangeDirection | null) => {
            if (next) setDirection(next);
          }}
          sx={{ mb: 1 }}
        >
          <ToggleButton value='all'>
            {t('netWorthChanges.all', 'All')}
          </ToggleButton>
          <ToggleButton value='gainers'>
            {t('netWorthChanges.gainers', 'Gainers')}
          </ToggleButton>
          <ToggleButton value='losers'>
            {t('netWorthChanges.losers', 'Losers')}
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Stack
        direction={isMobile ? 'column' : 'row'}
        justifyContent='space-between'
        gap={1.5}
        sx={{ mb: 2 }}
      >
        <Box sx={DASHBOARD_WIDGET_DATE_PICKER_SX}>
          <DateRange
            startDate={picker.draft?.startDate ?? period?.startDate}
            endDate={picker.draft?.endDate ?? period?.endDate}
            onChange={picker.onChange}
            onClose={picker.onClose}
            shortcutItems={picker.shortcutItems}
            selectedShortcutLabel={picker.selectedShortcutLabel}
            maxDate={picker.maxDate}
            useWidgetNarrowIconField={isWidget && isBelowMobileWidth}
            disabled={!isReady}
          />
        </Box>
        <TextField
          size='small'
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
          placeholder={searchLabel}
          slotProps={{
            htmlInput: { 'aria-label': searchLabel },
            input: {
              endAdornment: (
                <InputAdornment position='end'>
                  <SearchIcon fontSize='small' />
                </InputAdornment>
              ),
            },
          }}
          sx={{ minWidth: { sm: 240 } }}
        />
      </Stack>

      {renderBody()}

      <ClientDetailsFlyout
        open={openClientId !== null}
        client={openClientId !== null ? { InvestorId: openClientId } : null}
        toggleDrawer={(isOpen: boolean) => {
          if (!isOpen) setOpenClientId(null);
        }}
      />
    </ContentContainer>
  );
}
